import { BaseSocketService, SocketServiceConfig } from '../index';

// ----------- Type Definitions -----------
export interface SummarizationEvent {
    dealer_id?: string;
    conversation_id?: string;
    task_id?: string;
    event_type?: string;
    metrics?: {
        metric_type?: string;
        value?: number;
        timestamp?: string;
    };
    data_grid_update?: {
        headers?: any[];
        data?: any[];
        timestamp?: string;
    };
    nurturing_history?: {
        task_id?: string;
        status?: string;
        timestamp?: string;
        [key: string]: any;
    };
    task_html?: {
        task_id?: string;
        html_content?: string;
        s3_url?: string;
        timestamp?: string;
    };
    export_data?: {
        export_id?: string;
        status?: string;
        download_url?: string;
        timestamp?: string;
    };
    processing_type?: 'processing' | 'generating' | 'completed' | 'error';
    status?: 'success' | 'error' | 'processing' | 'completed';
    error?: {
        code: string;
        message: string;
        details?: any;
    };
    message?: string;
    timestamp: string;
    [key: string]: any;
}

export interface SummarizationSubscription {
    subscription_id: string;
    dealer_id?: string;
    conversation_id?: string;
    task_id?: string;
    callback: (data: SummarizationEvent) => void;
    active: boolean;
}

export interface SubscribeSummarizationRequest {
    dealer_id?: string;
    conversation_id?: string;
    task_id?: string;
    subscription_type?: 'dealer' | 'conversation' | 'task' | 'all';
    token?: string;
}

export interface UnsubscribeSummarizationRequest {
    subscription_id: string;
}

// ----------- SalesPortal Socket Service -----------
class SummarizationSocketService extends BaseSocketService {
    private static instance: SummarizationSocketService;
    private subscriptions: Map<string, SummarizationSubscription> = new Map();
    private eventCache: Map<string, SummarizationEvent> = new Map();

    constructor(config: SocketServiceConfig) {
        super(config);
    }

    // Singleton pattern
    static getInstance(config?: SocketServiceConfig): SummarizationSocketService {
        if (!SummarizationSocketService.instance) {
            if (!config) {
                throw new Error('SummarizationSocketService requires config for first initialization');
            }
            SummarizationSocketService.instance = new SummarizationSocketService(config);
        }
        return SummarizationSocketService.instance;
    }

    // Service name
    getServiceName(): string {
        return 'SummarizationSocketService';
    }

    // Events this service handles
    getEvents(): string[] {
        return [
            'metrics_update',           // Metrics updates
            'data_grid_update',         // Data grid updates
            'nurturing_history_update',  // Nurturing history updates
            'task_html_update',         // Task HTML updates
            'export_data_update',       // Export data updates
            'summarization_event',       // General summarization events
            'streaming_response',       // Streaming response events
            'processing_status',        // Processing status updates
            'subscribe_success',        // Subscription confirmation
            'subscribe_error',          // Subscription error
            'unsubscribe_success',      // Unsubscription confirmation
            'unsubscribe_error',        // Unsubscription error
        ];
    }

    // Subscribe to sales portal events
    subscribeToSummarization(
        subscriptionId: string,
        callback: (data: SummarizationEvent) => void,
        options?: {
            dealer_id?: string;
            conversation_id?: string;
            task_id?: string;
            subscription_type?: 'dealer' | 'conversation' | 'task' | 'all';
            token?: string;
        }
    ): boolean {
        if (!subscriptionId || typeof callback !== 'function') {
            console.error('❌ Invalid subscription ID or callback for subscription');
            return false;
        }

        // Check if already subscribed
        if (this.subscriptions.has(subscriptionId)) {
            console.log(`📡 Already subscribed: ${subscriptionId}`);
            return true;
        }

        // Ensure socket is connected before subscribing
        if (!this.isConnected()) {
            console.log('📡 Socket not connected, attempting to connect...');
            this.connect();
            
            // If still not connected after a short delay, the emit will queue the event
            // The base class handles queuing subscribe events
        }

        // Add to subscriptions map
        this.subscriptions.set(subscriptionId, {
            subscription_id: subscriptionId,
            dealer_id: options?.dealer_id,
            conversation_id: options?.conversation_id,
            task_id: options?.task_id,
            callback,
            active: true
        });

        // Emit subscribe event to server - pass conversation_id directly as array
        // Format: ["subscribe", conversation_id]
        const conversationId = options?.conversation_id || subscriptionId;
        this.emit('subscribe', conversationId);

        console.log(`📡 Subscribed to summarization: ${subscriptionId}`, {
            conversation_id: options?.conversation_id,
            dealer_id: options?.dealer_id,
            task_id: options?.task_id,
            subscription_type: options?.subscription_type || 'all',
            connected: this.isConnected()
        });
        return true;
    }

    // Unsubscribe from sales portal events
    unsubscribeFromSalesPortal(subscriptionId: string): boolean {
        if (!subscriptionId) {
            console.error('❌ Invalid subscription ID for unsubscription');
            return false;
        }

        const subscription = this.subscriptions.get(subscriptionId);
        if (!subscription) {
            console.log(`📡 No active subscription found: ${subscriptionId}`);
            return false;
        }

        // Mark as inactive
        subscription.active = false;

        // Emit unsubscribe event to server
        this.emit('unsubscribe', { subscription_id: subscriptionId });

        // Remove from subscriptions map
        this.subscriptions.delete(subscriptionId);

        console.log(`📡 Unsubscribed from sales portal: ${subscriptionId}`);
        return true;
    }

    // Get active subscriptions
    getActiveSubscriptions(): string[] {
        return Array.from(this.subscriptions.keys()).filter(id => {
            const sub = this.subscriptions.get(id);
            return sub?.active === true;
        });
    }

    // Check if subscribed
    isSubscribed(subscriptionId: string): boolean {
        const subscription = this.subscriptions.get(subscriptionId);
        return subscription?.active === true;
    }

    // Get subscription count
    getSubscriptionCount(): number {
        return this.subscriptions.size;
    }

    // Clear all subscriptions
    clearAllSubscriptions(): void {
        this.subscriptions.forEach((subscription, subscriptionId) => {
            this.unsubscribeFromSalesPortal(subscriptionId);
        });
        this.subscriptions.clear();
        console.log('📡 Cleared all sales portal subscriptions');
    }

    // Handle incoming events
    private handleIncomingEvent(eventName: string, data: any): void {
        try {
            // Add to event history
            this.addEventToHistory(eventName, data);

            // Handle subscription confirmation events
            if (eventName === 'subscribe_success' || eventName === 'subscribe_error') {
                console.log(`📡 Subscription ${eventName === 'subscribe_success' ? 'confirmed' : 'failed'}:`, data);
                return;
            }

            if (eventName === 'unsubscribe_success' || eventName === 'unsubscribe_error') {
                console.log(`📡 Unsubscription ${eventName === 'unsubscribe_success' ? 'confirmed' : 'failed'}:`, data);
                return;
            }

            // Process all other events as summarization events
            // Handle nested message structure from backend (if present)
            let eventData = data;
            if (data && data.message && typeof data.message === 'object') {
                eventData = { ...data, ...data.message };
            }

            // For streaming_response, extract conversation_id from message if present
            let conversationId = eventData.conversation_id;
            if (eventName === 'streaming_response' && data.message && typeof data.message === 'object') {
                const messageObj = data.message as any;
                if (messageObj.conversationId) {
                    conversationId = messageObj.conversationId;
                }
            }

            // Create SummarizationEvent object
            const summarizationEvent: SummarizationEvent = {
                dealer_id: eventData.dealer_id,
                conversation_id: conversationId || eventData.conversation_id,
                task_id: eventData.task_id,
                event_type: eventName,
                metrics: eventData.metrics,
                data_grid_update: eventData.data_grid_update,
                nurturing_history: eventData.nurturing_history,
                task_html: eventData.task_html,
                export_data: eventData.export_data,
                processing_type: eventData.processing_type,
                status: eventData.status || 'success',
                error: eventData.error,
                message: eventData.message || (eventName === 'streaming_response' ? data.message : undefined),
                timestamp: eventData.timestamp || new Date().toISOString(),
                ...eventData
            };

            // Broadcast to all relevant subscriptions
            this.broadcastToSubscriptions(summarizationEvent);
        } catch (error) {
            console.error(`❌ Error handling event '${eventName}':`, error);
        }
    }

    // Broadcast event to relevant subscriptions
    private broadcastToSubscriptions(eventData: SummarizationEvent): void {
        const dealerId = eventData.dealer_id;
        const conversationId = eventData.conversation_id;
        const taskId = eventData.task_id;

        // Find matching subscriptions
        this.subscriptions.forEach((subscription, subscriptionId) => {
            if (!subscription.active) {
                return;
            }

            // Check if subscription matches the event
            let shouldBroadcast = false;

            // If subscription has specific IDs, match them
            if (subscription.dealer_id && subscription.dealer_id === dealerId) {
                shouldBroadcast = true;
            } else if (subscription.conversation_id && subscription.conversation_id === conversationId) {
                shouldBroadcast = true;
            } else if (subscription.task_id && subscription.task_id === taskId) {
                shouldBroadcast = true;
            } else if (!subscription.dealer_id && !subscription.conversation_id && !subscription.task_id) {
                // Broadcast to all subscriptions without specific filters
                shouldBroadcast = true;
            }

            if (shouldBroadcast) {
                try {
                    subscription.callback(eventData);
                } catch (error) {
                    console.error(`❌ Error in subscription callback for ${subscriptionId}:`, error);
                }
            }
        });
    }

    // Override connect to set up event listeners
    connect(): void {
        super.connect();
        
        // Set up event listeners for all sales portal events
        this.getEvents().forEach(eventName => {
            this.on(eventName, (data) => {
                this.handleIncomingEvent(eventName, data);
            });
        });
    }

    // Override disconnect to clean up subscriptions
    disconnect(): void {
        this.clearAllSubscriptions();
        super.disconnect();
    }

    // Get cached events for a subscription
    getCachedEvents(subscriptionId?: string): SummarizationEvent[] {
        if (subscriptionId) {
            return Array.from(this.eventCache.values())
                .filter(event => {
                    const sub = this.subscriptions.get(subscriptionId);
                    if (!sub) return false;
                    return (
                        (sub.dealer_id && event.dealer_id === sub.dealer_id) ||
                        (sub.conversation_id && event.conversation_id === sub.conversation_id) ||
                        (sub.task_id && event.task_id === sub.task_id)
                    );
                })
                .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        }
        return Array.from(this.eventCache.values())
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }

    // Clear cached events for a subscription
    clearCachedEvents(subscriptionId?: string): void {
        if (subscriptionId) {
            const sub = this.subscriptions.get(subscriptionId);
            if (sub) {
                const eventsToDelete: string[] = [];
                this.eventCache.forEach((event, key) => {
                    if (
                        (sub.dealer_id && event.dealer_id === sub.dealer_id) ||
                        (sub.conversation_id && event.conversation_id === sub.conversation_id) ||
                        (sub.task_id && event.task_id === sub.task_id)
                    ) {
                        eventsToDelete.push(key);
                    }
                });
                eventsToDelete.forEach(key => this.eventCache.delete(key));
            }
        } else {
            this.eventCache.clear();
        }
    }

    // Get service status
    getServiceStatus(): {
        connected: boolean;
        subscriptionCount: number;
        activeSubscriptions: string[];
        eventHistoryCount: number;
    } {
        return {
            connected: this.isConnected(),
            subscriptionCount: this.getSubscriptionCount(),
            activeSubscriptions: this.getActiveSubscriptions(),
            eventHistoryCount: this.getEventHistory().length
        };
    }
}

// Export singleton instance
export const summarizationSocketService = SummarizationSocketService.getInstance;

// Export the class for testing
export { SummarizationSocketService };

// Types are already exported above as interfaces
